{
  "legal": {
    "privacy": {
      "title": "Privacy Policy",
      "effectiveDate": "Effective date: 1 January 2026",
      "intro": "nextsub (\"we\", \"us\", or \"our\") operates the nextsub control plane software and associated web properties. This Privacy Policy explains how we collect, use, and protect information when you use our service.",
      "sections": [
        {
          "title": "1. Information We Collect",
          "paragraphs": [
            "We collect information you provide directly when you create an account, including your email address and password hash. We also collect usage data generated by the service, including proxy node traffic reports, heartbeat telemetry, subscription usage records, and administrator activity logs.",
            "When you visit our website, we may automatically collect technical information such as your IP address, browser type, referring page, and pages visited, via Cloudflare's edge network."
          ]
        },
        {
          "title": "2. How We Use Your Information",
          "paragraphs": ["We use collected information to:"],
          "items": [
            "Authenticate users and maintain secure sessions",
            "Provide and operate the nextsub control plane",
            "Generate usage metering and billing records",
            "Detect and prevent abuse or unauthorized access",
            "Improve the service and diagnose technical issues"
          ]
        },
        {
          "title": "3. Data Sharing",
          "paragraphs": ["We do not sell your personal data. We may share data with:"],
          "entries": [
            {
              "lead": "Cloudflare",
              "body": "for edge delivery, DDoS protection, and Turnstile bot detection"
            },
            {
              "lead": "Service providers",
              "body": "infrastructure and hosting partners under confidentiality obligations"
            },
            {
              "lead": "Legal authorities",
              "body": "when required by applicable law or to protect rights and safety"
            }
          ]
        },
        {
          "title": "4. Cookies & Analytics",
          "paragraphs": [
            "We use browser local storage to persist authentication refresh tokens. We do not use third-party advertising cookies. Cloudflare may set cookies for security and performance purposes as part of its edge network operation."
          ]
        },
        {
          "title": "5. Data Retention",
          "paragraphs": [
            "Account data is retained for as long as your account is active. Usage and metering records are retained for up to 24 months for billing and audit purposes. You may request deletion of your account and associated personal data at any time by contacting us."
          ]
        },
        {
          "title": "6. Your Rights",
          "paragraphs": [
            "Depending on your jurisdiction, you may have the right to access, correct, or delete your personal data; to restrict or object to processing; and to data portability. To exercise these rights, contact us using the details below."
          ]
        },
        {
          "title": "7. Security",
          "paragraphs": [
            "We use industry-standard security measures including TLS encryption in transit, bcrypt password hashing, short-lived JWT access tokens, and Cloudflare WAF protection. No method of transmission or storage is 100% secure; we cannot guarantee absolute security."
          ]
        },
        {
          "title": "8. Changes to This Policy",
          "paragraphs": [
            "We may update this policy from time to time. We will notify users of material changes by posting the new policy on this page with an updated effective date."
          ]
        },
        {
          "title": "9. Contact",
          "paragraphs": ["For privacy inquiries, please contact a sales representative."]
        }
      ]
    },
    "terms": {
      "title": "Terms of Service",
      "effectiveDate": "Effective date: 1 January 2026",
      "intro": "By accessing or using the nextsub control plane (\"Service\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree to these Terms, do not use the Service.",
      "sections": [
        {
          "title": "1. Acceptance of Terms",
          "paragraphs": [
            "These Terms constitute a legally binding agreement between you (\"User\") and nextsub (\"Company\"). These Terms apply to all users of the Service, including operators, tenant administrators, and end users."
          ]
        },
        {
          "title": "2. Description of Service",
          "paragraphs": [
            "nextsub is a multi-tenant proxy-management control plane that enables operators to provision and manage proxy nodes, tenants, subscriptions, credentials, and usage metering. The Service is provided as software and as a hosted control plane accessible via the web interface and REST API."
          ]
        },
        {
          "title": "3. Acceptable Use",
          "paragraphs": ["You agree not to use the Service to:"],
          "items": [
            "Facilitate illegal activities or violate applicable law",
            "Distribute malware, conduct phishing, or engage in fraud",
            "Interfere with or disrupt the Service or its infrastructure",
            "Attempt unauthorized access to accounts or systems",
            "Circumvent geographic restrictions or sanctions in violation of law",
            "Resell or sublicense the Service without prior written consent"
          ],
          "closing": "The Company reserves the right to suspend or terminate accounts that violate this policy."
        },
        {
          "title": "4. Account Responsibilities",
          "paragraphs": [
            "You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized use of your account. The Company is not liable for losses resulting from unauthorized account access caused by your failure to safeguard credentials."
          ]
        },
        {
          "title": "5. Payment & Billing",
          "paragraphs": [
            "Paid plans are billed in advance on a monthly or annual basis. Fees are non-refundable except as required by law or as explicitly stated in a separate written agreement. Failure to pay may result in suspension of the Service. The Company reserves the right to modify pricing with 30 days' notice."
          ]
        },
        {
          "title": "6. Intellectual Property",
          "paragraphs": [
            "The Service and its underlying software, documentation, and trademarks are owned by the Company. You are granted a limited, non-exclusive, non-transferable license to use the Service in accordance with these Terms. You retain ownership of data you upload or generate through the Service."
          ]
        },
        {
          "title": "7. Limitation of Liability",
          "paragraphs": [
            "To the maximum extent permitted by law, the Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of or inability to use the Service, even if the Company has been advised of the possibility of such damages. The Company's total aggregate liability shall not exceed the fees paid by you in the 12 months preceding the claim."
          ]
        },
        {
          "title": "8. Disclaimer of Warranties",
          "paragraphs": [
            "The Service is provided \"as is\" and \"as available\" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. The Company does not warrant that the Service will be uninterrupted, error-free, or free of harmful components."
          ]
        },
        {
          "title": "9. Termination",
          "paragraphs": [
            "Either party may terminate the agreement at any time. The Company may suspend or terminate your access immediately for violations of these Terms or for non-payment. Upon termination, your right to use the Service ceases and the Company may delete your data after a grace period."
          ]
        },
        {
          "title": "10. Governing Law",
          "paragraphs": [
            "These Terms are governed by applicable law without regard to conflict-of-law principles. Any disputes shall be resolved through binding arbitration or in courts of competent jurisdiction as agreed in writing."
          ]
        },
        {
          "title": "11. Changes to Terms",
          "paragraphs": [
            "We may update these Terms from time to time. Continued use of the Service after changes become effective constitutes your acceptance of the revised Terms. We will provide at least 14 days' notice of material changes via email or in-app notification."
          ]
        },
        {
          "title": "12. Contact",
          "paragraphs": ["Questions about these Terms: please contact a sales representative."]
        }
      ]
    }
  },
  "audit": {
    "actions": {
      "auth": {
        "login": "User signed in",
        "refresh": "Sign-in session renewed",
        "logout": "User signed out"
      },
      "user": {
        "create": "User created",
        "update": "User updated",
        "delete": "User deleted",
        "enable": "User enabled",
        "disable": "User disabled",
        "disabled": "User disabled",
        "test": "User tested",
        "promote_to_tenant": "User promoted to tenant",
        "manager": {
          "update": "User manager changed"
        },
        "role": {
          "grant": "Tenant administrator role granted",
          "revoke": "Tenant administrator role revoked"
        },
        "profile": {
          "update": "Profile updated"
        }
      },
      "tenant": {
        "create": "Tenant created",
        "update": "Tenant updated",
        "delete": "Tenant deleted",
        "disabled": "Tenant disabled",
        "onboard": "Tenant onboarding completed"
      },
      "external_subscription": {
        "create": "External subscription created",
        "update": "External subscription updated",
        "delete": "External subscription deleted",
        "sync": "External subscription synced"
      },
      "billing": {
        "subscription": {
          "created": "Billing subscription created",
          "activated": "Billing subscription activated",
          "cancelled": "Billing subscription cancelled",
          "expired": "Billing subscription expired",
          "paused": "Billing subscription paused",
          "resumed": "Billing subscription resumed",
          "replaced": "Billing subscription replaced",
          "traffic_reset": "Subscription traffic reset",
          "derived_access_revoked": "Derived subscription access revoked",
          "derived_grant_created": "Derived subscription grant created"
        },
        "period": {
          "created": "Billing period created",
          "closed": "Billing period closed"
        },
        "runtime_binding": {
          "pending": "Runtime binding queued"
        },
        "node_group": {
          "rebuild": "Node group configuration rebuilt"
        },
        "package_access": {
          "rebuild": "Package access rebuilt"
        }
      },
      "subscription": {
        "activated": "Subscription activated",
        "disabled": "Subscription disabled",
        "reset": "Subscription period reset",
        "traffic_reset": "Subscription traffic reset"
      },
      "subscription_link": {
        "create": "Subscription link created",
        "patch": "Subscription link updated",
        "delete": "Subscription link deleted",
        "rotate_secret": "Subscription link secret rotated",
        "rebind": "Subscription link rebound"
      },
      "user_credential": {
        "create": "User credential created"
      },
      "credential_domain": {
        "create": "Credential domain created"
      },
      "package": {
        "create": "Package created",
        "update": "Package updated",
        "publish": "Package published",
        "archive": "Package archived",
        "delete": "Package deleted"
      },
      "tenant_package": {
        "create": "Tenant package created",
        "update": "Tenant package updated",
        "publish": "Tenant package published",
        "archive": "Tenant package archived",
        "enable": "Tenant package enabled",
        "disable": "Tenant package disabled",
        "delete": "Tenant package deleted"
      },
      "node": {
        "create": "Server node created",
        "update": "Server node updated",
        "delete": "Server node deleted",
        "test": "Server node tested"
      },
      "provisioning_bundle": {
        "create": "Node provisioning bundle created",
        "reissue": "Node provisioning bundle reissued"
      },
      "enrollment_token": {
        "generate": "Enrollment token generated",
        "consume": "Enrollment token consumed",
        "action": "Enrollment token status changed"
      },
      "global_node_group": {
        "create": "Global node group created",
        "update": "Global node group updated",
        "delete": "Global node group deleted"
      },
      "global_node_group_member": {
        "add": "Node group member added",
        "update": "Node group member updated",
        "remove": "Node group member removed"
      },
      "tenant_node_group": {
        "create": "Tenant node group created",
        "delete": "Tenant node group deleted"
      },
      "node_selection": {
        "replace": "Node selection updated"
      },
      "grant": {
        "create": "Grant created",
        "update": "Grant updated",
        "delete": "Grant deleted"
      },
      "security_deny": {
        "create": "Deny rule created",
        "update": "Deny rule updated",
        "delete": "Deny rule deleted"
      },
      "security": {
        "deny": "Access denied"
      },
      "announcement": {
        "create": "Announcement created",
        "update": "Announcement updated",
        "publish": "Announcement published",
        "archive": "Announcement archived",
        "delete": "Announcement deleted"
      },
      "routing_policy": {
        "create": "Routing policy created",
        "update": "Routing policy updated",
        "rollback": "Routing policy rolled back",
        "delete": "Routing policy deleted"
      },
      "routing_resource": {
        "create": "Routing resource created",
        "update": "Routing resource updated",
        "delete": "Routing resource deleted"
      },
      "manifest": {
        "ack": "Node manifest acknowledged",
        "heartbeat": "Node heartbeat reported"
      },
      "boot_session": {
        "close": "Boot session closed"
      },
      "metering": {
        "sequence_gap": {
          "detected": "Usage sequence gap detected"
        },
        "receipt": {
          "late_covered": "Late usage receipt covered"
        }
      },
      "runtime": {
        "manifest": {
          "build_requested": "Node manifest build requested"
        },
        "subscription": {
          "activate": "Subscription activation queued",
          "disable": "Subscription disable queued",
          "prepare_period": "Subscription period preparation queued"
        },
        "user": {
          "disable": "User disable queued"
        }
      },
      "traffic_report": {
        "project_tsdb": "Usage projected to time-series storage"
      },
      "global_routing_policy": {
        "publish": "Global routing published"
      }
    },
    "resources": {
      "announcement": "Announcement",
      "billing_period": "Billing period",
      "boot_session": "Boot session",
      "credential_domain": "Credential domain",
      "enrollment_token": "Enrollment token",
      "external_subscription": "External subscription",
      "global_node_group": "Global node group",
      "global_node_group_member": "Node group member",
      "global_routing_policy": "Global routing policy",
      "grant": "Grant",
      "manifest": "Node manifest",
      "node": "Server node",
      "node_selection": "Node selection",
      "package": "Package",
      "provisioning_bundle": "Provisioning bundle",
      "routing_external_resource": "Routing resource",
      "security_deny": "Deny rule",
      "session": "Sign-in session",
      "subscription": "Subscription",
      "subscription_link": "Subscription link",
      "tenant": "Tenant",
      "tenant_node_group": "Tenant node group",
      "traffic_routing_policy": "Traffic routing policy",
      "unknown": "Unknown resource",
      "usage_record": "Usage record",
      "user": "User",
      "user_credential": "User credential"
    },
    "actors": {
      "user": "User",
      "anonymous": "Signed-out user",
      "system": "System",
      "worker": "Background job",
      "agent": "Node agent",
      "service": "System service",
      "operator": "Operator"
    },
    "outcomes": {
      "success": "Succeeded",
      "failure": "Failed",
      "error": "Error",
      "denied": "Denied"
    },
    "summaries": {
      "login": "{{email}} signed in",
      "sessionRefreshed": "The sign-in session was renewed automatically",
      "logout": "The user signed out",
      "externalSync": "Imported {{imported}} nodes, skipped {{skipped}} · {{format}} format",
      "externalSyncFailure": "Sync failed: {{reason}}",
      "managerAssigned": "Set {{manager}} as the manager for {{user}}",
      "managerCleared": "Removed the manager from {{user}}",
      "failure": "Failure reason: {{reason}}",
      "entityCreated": "Created {{entity}}",
      "entityDeleted": "Deleted {{entity}}",
      "changedFields": "Changed: {{fields}}",
      "revision": "Updated to revision {{revision}}",
      "target": "Object: {{entity}}"
    },
    "fields": {
      "id": "ID",
      "tenant_id": "Tenant ID",
      "user_id": "User ID",
      "node_id": "Node ID",
      "email": "Email",
      "display_name": "Display name",
      "name": "Name",
      "title": "Title",
      "status": "Status",
      "roles": "Roles",
      "managed_by": "Manager",
      "manager_user_id": "Manager ID",
      "format": "Format",
      "revision": "Revision",
      "reason": "Reason",
      "imported": "Imported nodes",
      "skipped": "Skipped nodes",
      "detected_format": "Detected format",
      "session_id": "Previous session ID",
      "rotated_session_id": "New session ID",
      "created_at": "Created at",
      "updated_at": "Updated at",
      "token_version": "Sign-in version",
      "scope": "Scope",
      "generation": "Generation"
    },
    "values": {
      "yes": "Yes",
      "no": "No",
      "none": "None",
      "unknown": "Unknown",
      "active": "Active",
      "disabled": "Disabled",
      "success": "Succeeded",
      "failure": "Failed",
      "tenant_admin": "Tenant administrator",
      "super_admin": "Super administrator",
      "user": "User",
      "uri": "URI",
      "base64": "Base64",
      "fetch_failed": "Could not fetch subscription",
      "invalid_source": "Source refused (bad content or private address)",
      "internal": "Internal system error"
    },
    "listSeparator": ", "
  }
}
